/**
 * B2B の利用申請（ops-db `b2b_applications`）の **語彙と純粋ロジック**。
 *
 * 読み手は 2 つ: 公開面の kalori-api（`POST /api/b2b/apply` = 受付・検証・シャドー判定）と
 * admin（`/b2b/applications` = 審査画面のラベル・却下理由）。同じ enum を 2 か所に literal で
 * 持つと、片方に値を足した日に他方が「不明」を表示し始めるのでここに 1 つ置く。
 *
 * 設計 = `docs/architecture/b2b-onboarding.md`。
 *
 * ⚠️ **ここに DB アクセスやメール送信を入れない**（web のフォームからも型だけ import できる状態を保つ）。
 */
/** 事業線。migration `0005_b2b_applications.sql` の CHECK と対。 */
export const B2B_APPLICATION_LINES = ["developer", "trainer", "merchant"];
export function isB2BApplicationLine(v) {
    return typeof v === "string" && B2B_APPLICATION_LINES.includes(v);
}
export const B2B_APPLICATION_STATUSES = ["pending", "approved", "rejected"];
/**
 * 却下理由。**自由記述ではなく enum にしてある**のは、後で `auto_checks` と突き合わせて
 * 「機械が弾けたはずの却下がどれだけあるか」を数えるため（自由記述は集計できない）。
 * 補足は `review_note` に書く。
 */
export const B2B_REJECT_REASONS = {
    not_target: "対象外（用途が合わない）",
    competitor: "競合",
    spam: "スパム・いたずら",
    incomplete: "情報不足（会社名・用途が無い）",
    duplicate: "重複申請",
    other: "その他（メモ参照）",
};
export function isB2BRejectReason(v) {
    return typeof v === "string" && Object.prototype.hasOwnProperty.call(B2B_REJECT_REASONS, v);
}
/**
 * 面固有の項目（`attrs` JSON）。キーは面ごとに固定し、値は短い文字列だけ。
 * コンソールの申請フォームが聞く設問（`../b2b/apply` の `B2B_APPLY_QUESTIONS`）のキーは全部ここに載せる
 * （載っていないキーは受付で黙って落ちる）。`product_type` / `business_type` は LP のフォーム時代のキー。
 */
export const B2B_ATTR_KEYS = {
    developer: ["applicant_type", "expected_mau", "product_type", "reference_mode", "fields_fit", "fields_extra", "attribution", "caller", "calls_per_user", "start_timing"],
    trainer: ["clients", "business_type"],
    merchant: ["inquiry", "store_count"],
};
export const B2B_APPLICATION_LIMITS = {
    email: 254,
    orgName: 100,
    contactName: 100,
    website: 200,
    useCase: 2000,
    attrValue: 100,
    termsVersion: 40,
};
/**
 * 用途（`use_case`・自由記述）を必須にする線。developer / trainer は審査で唯一「読む」項目が用途で、空だと結局
 * メールで聞き直す往復が要る（= 情報不足で却下する申請を入口で減らす）。merchant は必須の設問「ご相談内容」
 * （`inquiry`）が同じ役を持つので任意にした（2026-10-07・オーナー判断）。
 */
export const B2B_USE_CASE_REQUIRED = { developer: true, trainer: true, merchant: false };
/**
 * 公開フォームからの body を検証して正規化する。
 *
 * 必須は line / email / org_name と、線によって use_case（{@link B2B_USE_CASE_REQUIRED}）。
 */
export function parseB2BApplication(raw) {
    if (!raw || typeof raw !== "object")
        return { ok: false, error: "Invalid body" };
    const b = raw;
    if (!isB2BApplicationLine(b.line))
        return { ok: false, error: "Invalid line" };
    const email = str(b.email)?.toLowerCase() ?? "";
    if (!email || email.length > B2B_APPLICATION_LIMITS.email || !isPlausibleEmail(email)) {
        return { ok: false, error: "Invalid email" };
    }
    const orgName = clip(str(b.org_name), B2B_APPLICATION_LIMITS.orgName);
    if (!orgName)
        return { ok: false, error: "org_name is required" };
    const useCase = clip(str(b.use_case), B2B_APPLICATION_LIMITS.useCase);
    if (!useCase && B2B_USE_CASE_REQUIRED[b.line])
        return { ok: false, error: "use_case is required" };
    const contactName = clip(str(b.contact_name), B2B_APPLICATION_LIMITS.contactName);
    const websiteRaw = clip(str(b.website), B2B_APPLICATION_LIMITS.website);
    const website = websiteRaw ? normalizeWebsite(websiteRaw) : null;
    if (websiteRaw && !website)
        return { ok: false, error: "Invalid website" };
    const attrs = {};
    if (b.attrs && typeof b.attrs === "object") {
        for (const key of B2B_ATTR_KEYS[b.line]) {
            const v = clip(str(b.attrs[key]), B2B_APPLICATION_LIMITS.attrValue);
            if (v)
                attrs[key] = v;
        }
    }
    return {
        ok: true,
        value: {
            line: b.line,
            email,
            orgName,
            contactName,
            website,
            useCase,
            attrs,
            locale: b.locale === "en" ? "en" : "ja",
            termsVersionSeen: clip(str(b.terms_version), B2B_APPLICATION_LIMITS.termsVersion),
        },
    };
}
function str(v) {
    return typeof v === "string" ? v.trim() : null;
}
function clip(v, max) {
    if (!v)
        return null;
    return v.length > max ? v.slice(0, max) : v;
}
/** 厳密な RFC ではなく「@ の両側に何かあり、ドメインにドットがある」程度。誤検知で正規の申請を落とす方が損。 */
function isPlausibleEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
/** `example.co.jp` / `http://…` を `https://example.co.jp` に揃える。URL として壊れていれば null。 */
export function normalizeWebsite(input) {
    const withScheme = /^https?:\/\//i.test(input) ? input : `https://${input}`;
    try {
        const u = new URL(withScheme);
        if (u.protocol !== "https:" && u.protocol !== "http:")
            return null;
        if (!u.hostname.includes("."))
            return null;
        return u.toString();
    }
    catch {
        return null;
    }
}
// =============================================================
// シャドー判定（auto_checks）
// =============================================================
/**
 * 個人用途で使われる主要なフリーメール。**法人ドメインかどうかの近似**で、
 * これ自体は却下条件ではない（個人開発者も歓迎する面がある）。
 */
const FREE_MAIL_DOMAINS = new Set([
    "gmail.com",
    "googlemail.com",
    "yahoo.co.jp",
    "yahoo.com",
    "ymail.ne.jp",
    "hotmail.com",
    "hotmail.co.jp",
    "outlook.com",
    "outlook.jp",
    "live.jp",
    "live.com",
    "icloud.com",
    "me.com",
    "mac.com",
    "docomo.ne.jp",
    "ezweb.ne.jp",
    "au.com",
    "softbank.ne.jp",
    "i.softbank.jp",
    "protonmail.com",
    "proton.me",
    "aol.com",
    "nifty.com",
    "excite.co.jp",
]);
export function computeB2BAutoChecks(app, ctx) {
    const emailDomain = app.email.split("@")[1]?.toLowerCase() ?? "";
    let domainMatchesWebsite = null;
    if (app.website) {
        try {
            const host = new URL(app.website).hostname.toLowerCase();
            domainMatchesWebsite = host === emailDomain || host.endsWith(`.${emailDomain}`) || emailDomain.endsWith(`.${host}`);
        }
        catch {
            domainMatchesWebsite = null;
        }
    }
    const useCase = app.useCase ?? "";
    return {
        free_mail_domain: FREE_MAIL_DOMAINS.has(emailDomain),
        domain_matches_website: domainMatchesWebsite,
        duplicate_email: ctx.duplicateEmail,
        use_case_length: useCase.length,
        use_case_has_url: /https?:\/\/|www\./i.test(useCase),
        version: 1,
    };
}
//# sourceMappingURL=index.js.map